import { registerModal, useModal } from '@gluedigital/modal';
import { Suspense, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import ErrorBoundary from 'src/components/ErrorBoundary/ErrorBoundary';
import { UTM_MEDIUM, UTM_SOURCE } from 'src/constants/utm';
import { useShareId } from 'src/hooks/api';
import { useIsBubbleWidget } from 'src/hooks/useIsBubbleWidget';
import { useIsEmbedded } from 'src/hooks/useIsEmbedded';
import { useSuccessToast } from 'src/hooks/useSuccessToast';
import { CopySources, Message } from 'src/types';
import ShareButton from './ShareButton';
import ShareEntireChat from './ShareEntireChat';
import ShareIcons from './ShareIcons';
import ShareQuestion from './ShareQuestion';

import './ShareModal.sass';
import Loading from '@components/Loading/Loading';

export enum ShareEnum {
	QUESTION = 'question',
	CHAT = 'chat'
}

interface SharerProps {
	id?: string;
	handleCopy: (source: Required<CopySources>, text: string) => Promise<void>;
	text: string;
	title: string;
}

interface SharerDefaultProps {
	chatId?: string;
	handleCopy: (source: Required<CopySources>, text: string) => Promise<void>;
	text: string;
	title: string;
}

const ShareDefaultChatFooter = ({ handleCopy, title, text, chatId }: SharerDefaultProps) => {
	const shareId = chatId;
	const hasNavigator = typeof navigator !== 'undefined' && 'share' in navigator;
	const sharedContentUrl = `${window.location.origin}/share/${shareId}?utm_source=${UTM_SOURCE}&utm_medium=${UTM_MEDIUM}`;
	return (
		<footer id="share-modal-footer">
			{hasNavigator ? (
				<ShareButton url={sharedContentUrl} title={title} text={text} />
			) : (
				<ShareIcons url={sharedContentUrl} handleCopy={handleCopy} />
			)}
		</footer>
	);
};

const ShareFooter = ({ id, handleCopy, title, text }: SharerProps) => {
	const shareId = useShareId(id as string);
	const hasNavigator = typeof navigator !== 'undefined' && 'share' in navigator;
	const sharedContentUrl = `${window.location.origin}/share/${shareId}?utm_source=${UTM_SOURCE}&utm_medium=${UTM_MEDIUM}`;
	return (
		<footer id="share-modal-footer">
			{hasNavigator ? (
				<ShareButton url={sharedContentUrl} title={title} text={text} />
			) : (
				<ShareIcons url={sharedContentUrl} handleCopy={handleCopy} />
			)}
		</footer>
	);
};

const ShareModal = ({ message, replyTo }: { message?: Message; replyTo?: Message }) => {
	const { handleCopy } = useSuccessToast();
	const storeMessages = useSelector((s: { messages: Message[] }) => s.messages);
	const isBubbleWidget = useIsBubbleWidget();
	const isEmbedded = useIsEmbedded();
	const hideShareFooter = isBubbleWidget || isEmbedded;
	const modal = useModal();
	const { id: chatId } = useParams<{ id: string }>();
	let userAnswer, botReply, id;
	if (replyTo) {
		({ text: userAnswer } = replyTo);
	}
	if (message) {
		({ text: botReply, id } = message);
	}
	const shareOnlyChat = !userAnswer || !botReply || !id;
	const [shareType, setShareType] = useState<ShareEnum>(shareOnlyChat ? ShareEnum.CHAT : ShareEnum.QUESTION);
	const isChat = shareType === ShareEnum.CHAT;
	const isQuestion = shareType === ShareEnum.QUESTION;

	return (
		<div id="share-modal-content" className="modal-content">
			<header id="share-modal-header">
				<div>
					<h1>
						<span>
							<FormattedMessage id="share-modal.title" />
						</span>
					</h1>
					<button onClick={() => modal.hide()}>
						<span className="icon icon-cross" />
					</button>
				</div>
				<section id="share-selector">
					<label>
						<input
							disabled={shareOnlyChat}
							type="radio"
							checked={isQuestion}
							name="shareType"
							value={ShareEnum.QUESTION}
							onChange={() => setShareType(ShareEnum.QUESTION)}
						/>
						<span>
							<FormattedMessage id="share-modal.share-selector.question" />
						</span>
					</label>
					<label>
						<input
							disabled={!shareOnlyChat && storeMessages.length < 4}
							type="radio"
							checked={isChat}
							name="shareType"
							value={ShareEnum.CHAT}
							onChange={() => (shareOnlyChat || storeMessages.length > 3) && setShareType(ShareEnum.CHAT)}
						/>
						<span>
							<FormattedMessage id="share-modal.share-selector.chat" />
						</span>
					</label>
				</section>
			</header>
			<section id="share-modal-main">
				{isChat ? <ShareEntireChat storeMessages={storeMessages} /> : <ShareQuestion userAnswer={userAnswer} botReply={botReply} />}
			</section>
			<ErrorBoundary fallback={<footer id="share-modal-footer" />}>
				<Suspense
					fallback={
						<footer id="share-modal-footer">
							<Loading />
						</footer>
					}
				>
					{!hideShareFooter &&
						(isChat && chatId ? (
							<ShareDefaultChatFooter chatId={chatId} handleCopy={handleCopy} title={userAnswer} text={botReply} />
						) : (
							<ShareFooter id={shareType === ShareEnum.QUESTION ? id : null} handleCopy={handleCopy} title={userAnswer} text={botReply} />
						))}
				</Suspense>
			</ErrorBoundary>
		</div>
	);
};

registerModal('share-modal', ShareModal);
